import { useEffect, useState } from 'react';
import {
  apiSaveMarketingTag,
  apiSetServerSideConversions,
  type MarketingSettingsView,
  type TagConfigView,
} from '~/lib/api/marketing';
import { classNames } from '~/utils/classNames';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CARD,
  ErrorBanner,
  Field,
  Pill,
  Section,
  Spinner,
  Switch,
} from '~/components/workbench/tools/payments/ui';
import {
  apiErrorMessage,
  consentCategoryLabel,
  serverSideReady,
  TAG_SPECS,
  tagDirty,
  tagDraft,
  tagInput,
  tagProblems,
  tagStatusMeta,
  type TagDraft,
  type TagSpec,
} from './marketing-model';

// Literal class names: UnoCSS only scans .tsx, so the catalog's `icon` strings (in a .ts package) never get CSS.
const TAG_ICONS: Record<string, string> = {
  ga4: 'i-ph:chart-line-up',
  'google-ads': 'i-ph:google-logo',
  'meta-pixel': 'i-ph:meta-logo',
  'tiktok-pixel': 'i-ph:tiktok-logo',
  'linkedin-insight': 'i-ph:linkedin-logo',
  'microsoft-clarity': 'i-ph:cursor-click',
  'google-tag-manager': 'i-ph:tag',
};

type SetView = (next: MarketingSettingsView | ((prev: MarketingSettingsView | null) => MarketingSettingsView)) => void;

export function TrackingView({
  appId,
  view,
  onChange,
}: {
  appId: string;
  view: MarketingSettingsView;
  onChange: SetView;
}) {
  return (
    <>
      <div className="ui-banner-info items-start leading-relaxed">
        <div className="i-ph:info mt-px shrink-0 text-sm text-info" />
        <span className="text-[12px] text-secondary">
          Tags load only on your published site, after your next publish. They never load in the preview, so building
          and testing doesn't pollute your analytics or ad accounts. Each tag waits for the visitor's consent.
        </span>
      </div>

      <ServerSideCard appId={appId} view={view} onChange={onChange} />

      <Section title="Tags" icon="i-ph:tag">
        {TAG_SPECS.map((spec) => (
          <TagCard
            key={spec.id}
            appId={appId}
            spec={spec}
            saved={view.tags.find((t) => t.provider === spec.id)}
            onSaved={(tag) =>
              onChange((prev) => {
                const base = prev ?? view;
                const others = base.tags.filter((t) => t.provider !== tag.provider);

                return { ...base, tags: [...others, tag] };
              })
            }
          />
        ))}
      </Section>
    </>
  );
}

function ServerSideCard({ appId, view, onChange }: { appId: string; view: MarketingSettingsView; onChange: SetView }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ready = serverSideReady(view.tags);

  const toggle = async (enabled: boolean) => {
    setSaving(true);
    setError(null);

    try {
      const res = await apiSetServerSideConversions(appId, enabled);
      onChange((prev) => ({ ...(prev ?? view), serverSideConversions: res.enabled }));
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not change server-side conversions.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="Server-side conversions" icon="i-ph:cloud-arrow-up">
      <div className={classNames(CARD, 'space-y-2.5 p-4')}>
        <div className="flex items-center justify-between gap-3">
          <Switch
            checked={view.serverSideConversions}
            onChange={(next) => void toggle(next)}
            disabled={saving}
            label="Send purchases and leads from the server"
          />
          {saving && <Spinner />}
        </div>
        <p className="text-[11px] leading-relaxed text-muted">
          Sends purchases to Meta (Conversions API) and GA4 (Measurement Protocol) from Dunea, so ad blockers can't drop
          them. Only for visitors who consented.{' '}
          {ready.length
            ? `Ready for ${ready.join(' and ')}.`
            : 'Add the GA4 API secret or the Meta access token below to use it.'}
        </p>
        {error && <ErrorBanner message={error} />}
      </div>
    </Section>
  );
}

function TagCard({
  appId,
  spec,
  saved,
  onSaved,
}: {
  appId: string;
  spec: TagSpec;
  saved: TagConfigView | undefined;
  onSaved: (tag: TagConfigView) => void;
}) {
  const [draft, setDraft] = useState<TagDraft>(() => tagDraft(spec, saved));
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);

  // A save (here or elsewhere) resets the form to the server's values; typed secrets are dropped.
  useEffect(() => {
    setDraft(tagDraft(spec, saved));
    setTouched({});
  }, [spec, saved]);

  const problems = tagProblems(spec, draft);
  const dirty = tagDirty(spec, draft, saved);
  const valid = Object.keys(problems).length === 0;
  const status = tagStatusMeta(saved);
  const open = draft.enabled || Boolean(saved);

  const update = (patch: Partial<TagDraft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setJustSaved(false);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(Object.fromEntries(spec.fields.map((f) => [f.key, true])));

    if (!dirty || !valid) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const tag = await apiSaveMarketingTag(appId, spec.id, tagInput(spec, draft));
      setJustSaved(true);
      onSaved(tag);
    } catch (err) {
      setError(apiErrorMessage(err, `Could not save ${spec.title}.`));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className={classNames(CARD, 'p-4')}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-white/[0.04] ring-1 ring-inset ring-white/[0.06]">
            <div className={classNames(TAG_ICONS[spec.id] ?? 'i-ph:tag', 'text-[15px] text-secondary')} />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[13px] font-medium text-primary">{spec.title}</span>
              <Pill tone={spec.consent === 'analytics' ? 'info' : 'muted'} label={consentCategoryLabel(spec.consent)} />
            </div>
            <p className="mt-0.5 text-[11px] leading-relaxed text-muted">{spec.description}</p>
          </div>
        </div>
        <Pill tone={status.tone} label={status.label} />
      </div>

      <div className="mt-3">
        <Switch checked={draft.enabled} onChange={(enabled) => update({ enabled })} label="Load on my site" />
      </div>

      {open && (
        <div className="mt-3 space-y-3 border-t border-hairline pt-3">
          {spec.fields.map((f) => {
            const stored = Boolean(f.secret && saved?.secretsSet.includes(f.key));
            const clearing = draft.clear.includes(f.key);
            const value = (f.secret ? draft.secrets[f.key] : draft.values[f.key]) ?? '';
            const problem = touched[f.key] || value ? problems[f.key] : undefined;

            return (
              <Field
                key={f.key}
                label={f.label}
                hint={
                  problem ? (
                    <span className="text-danger/90">{problem}</span>
                  ) : f.secret ? (
                    'Write-only. Stored encrypted and never shown again.'
                  ) : undefined
                }
              >
                <div className="flex items-center gap-2">
                  <input
                    type={f.secret ? 'password' : 'text'}
                    value={value}
                    autoComplete="off"
                    spellCheck={false}
                    onChange={(e) =>
                      f.secret
                        ? update({
                            secrets: { ...draft.secrets, [f.key]: e.target.value },
                            clear: draft.clear.filter((k) => k !== f.key),
                          })
                        : update({ values: { ...draft.values, [f.key]: e.target.value } })
                    }
                    onBlur={() => setTouched((t) => ({ ...t, [f.key]: true }))}
                    placeholder={stored && !clearing ? 'Saved. Type to replace' : f.placeholder}
                    aria-invalid={Boolean(problem)}
                    className={classNames('ui-input-mono min-w-0 flex-1', problem && '!border-danger/50')}
                  />
                  {stored && !clearing && <Pill tone="success" label="Saved" />}
                  {stored && (
                    <button
                      type="button"
                      onClick={() =>
                        update({
                          clear: clearing ? draft.clear.filter((k) => k !== f.key) : [...draft.clear, f.key],
                          secrets: { ...draft.secrets, [f.key]: '' },
                        })
                      }
                      className={BTN_GHOST}
                    >
                      {clearing ? 'Keep' : 'Remove'}
                    </button>
                  )}
                </div>
              </Field>
            );
          })}

          {error && <ErrorBanner message={error} />}

          <div className="flex items-center gap-3">
            <button type="submit" disabled={saving || !dirty || !valid} className={BTN_SECONDARY}>
              {saving && <Spinner />}
              {saving ? 'Saving…' : 'Save'}
            </button>
            {justSaved && !dirty && <span className="text-[11px] text-muted">Saved. Publish to apply.</span>}
            {dirty && !saving && <span className="text-[11px] text-muted">Unsaved changes</span>}
          </div>
        </div>
      )}
    </form>
  );
}
